import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, ArrowLeft } from 'lucide-react';
import AutoTranslatedText from '../components/AutoTranslatedText';
import { parseLocalDate } from '../utils/dateUtils';

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events', error);
    } finally {
      setLoading(false);
    }
  };

  // Get status color and styling
  const getStatusStyle = (status) => {
    const styles = {
      live: {
        bg: 'bg-green-500',
        text: 'text-white',
        border: 'border-green-600',
        dot: 'bg-green-500',
        label: t('calendar.live')
      },
      registration_open: {
        bg: 'bg-amber-500',
        text: 'text-white',
        border: 'border-amber-600',
        dot: 'bg-amber-500',
        label: t('calendar.registration_open')
      },
      upcoming: {
        bg: 'bg-blue-500',
        text: 'text-white',
        border: 'border-blue-600',
        dot: 'bg-blue-500',
        label: t('calendar.upcoming')
      },
      completed: {
        bg: 'bg-slate-400',
        text: 'text-white',
        border: 'border-slate-500',
        dot: 'bg-slate-400',
        label: t('calendar.completed')
      }
    };
    return styles[status] || styles.upcoming;
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString(i18n.language || 'en', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get events for a specific date
  const getEventsForDate = (day) => {
    const dateStr = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
    return events.filter(event => {
      const startDate = parseLocalDate(event.start_date);
      const endDate = parseLocalDate(event.end_date);
      const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      return checkDate >= new Date(startDate.toDateString()) && checkDate <= new Date(endDate.toDateString());
    });
  };

  // Check if date is today
  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const weekDays = [
    t('calendar.sun'), 
    t('calendar.mon'), 
    t('calendar.tue'), 
    t('calendar.wed'), 
    t('calendar.thu'), 
    t('calendar.fri'), 
    t('calendar.sat')
  ];
  const calendarDays = generateCalendarDays();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="calendar-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/events" className="inline-flex items-center text-slate-600 hover:text-primary mb-4 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('calendar.back_to_events')}
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center">
                <CalendarIcon className="h-8 w-8 mr-3 text-primary" />
                {t('calendar.title')}
              </h1>
              <p className="text-slate-600 mt-1">{t('calendar.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('calendar.status_legend')}</h3>
          <div className="flex flex-wrap gap-4">
            {['live', 'registration_open', 'upcoming', 'completed'].map(status => {
              const style = getStatusStyle(status);
              return (
                <div key={status} className="flex items-center space-x-2">
                  <span className={`w-4 h-4 rounded ${style.bg}`}></span>
                  <span className="text-sm text-slate-600">{style.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToToday}>
                {t('calendar.today')}
              </Button>
            </div>
            <h2 className="text-xl font-bold text-slate-900">{formatMonthYear(currentDate)}</h2>
            <div className="w-32"></div> {/* Spacer for alignment */}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <p className="text-slate-600">{t('calendar.loading')}</p>
            </div>
          ) : (
            <>
              {/* Week day headers */}
              <div className="grid grid-cols-7 border-b border-slate-200">
                {weekDays.map(day => (
                  <div key={day} className="p-3 text-center text-sm font-semibold text-slate-600 bg-slate-50 border-r border-slate-200 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, index) => {
                  const dayEvents = day ? getEventsForDate(day) : [];
                  const today = isToday(day);
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[120px] p-2 border-r border-b border-slate-200 last:border-r-0 ${
                        day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                      } ${today ? 'ring-2 ring-inset ring-primary' : ''}`}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium mb-1 ${today ? 'text-primary' : 'text-slate-700'}`}>
                            {day}
                            {today && <span className="ml-1 text-xs text-primary">({t('calendar.today')})</span>}
                          </div>
                          <div className="space-y-1">
                            {dayEvents.slice(0, 3).map(event => {
                              const style = getStatusStyle(event.status);
                              return (
                                <Link
                                  key={event.id}
                                  to={`/events/${event.id}`}
                                  className={`block px-2 py-1 rounded text-xs ${style.bg} ${style.text} truncate hover:opacity-90 transition-opacity`}
                                  title={event.name}
                                >
                                  {event.name}
                                </Link>
                              );
                            })}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-slate-500 pl-2">
                                +{dayEvents.length - 3} {t('calendar.more')}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Events List Below Calendar */}
        <div className="mt-8">
          <h3 className="text-xl font-bold text-slate-900 mb-4">{t('calendar.all_events_this_month')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.filter(event => {
              const startDate = parseLocalDate(event.start_date);
              const endDate = parseLocalDate(event.end_date);
              const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
              const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
              return (startDate <= monthEnd && endDate >= monthStart);
            }).map(event => {
              const style = getStatusStyle(event.status);
              return (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-slate-900">
                      <AutoTranslatedText text={event.name} translations={event.name_translations} showLoading={false} />
                    </h4>
                    <span className={`px-2 py-0.5 rounded text-xs ${style.bg} ${style.text}`}>
                      {style.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                    <AutoTranslatedText text={event.description} translations={event.description_translations} showLoading={false} />
                  </p>
                  <div className="text-xs text-slate-500">
                    <CalendarIcon className="inline h-3 w-3 mr-1" />
                    {parseLocalDate(event.start_date).toLocaleDateString(i18n.language)} - {parseLocalDate(event.end_date).toLocaleDateString(i18n.language)}
                  </div>
                </Link>
              );
            })}
            {events.filter(event => {
              const startDate = parseLocalDate(event.start_date);
              const endDate = parseLocalDate(event.end_date);
              const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
              const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
              return (startDate <= monthEnd && endDate >= monthStart);
            }).length === 0 && (
              <div className="col-span-full text-center py-8 text-slate-500">
                {t('calendar.no_events_this_month')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
